import { VOTER_GUIDE_SFMC_FORM_PROXY } from 'config/subscriptionForms';

/**
 * One signup, with field names matching the columns of the
 * `Emails_From_Branch` data extension one for one — `emailaddress`, not
 * `email`. See README § Email capture → SFMC before renaming any of them.
 *
 * The reader's street address and the ballot's districts are deliberately not
 * here. SFMC gets at most the ZIP code the reader typed, and nothing finer.
 */
export interface Signup {
  emailaddress: string;
  /** Branch ballot `_id`. With `lock`, it is what makes the emailed link work. */
  ballot: string;
  lock: string;
  firstname?: string;
  lastname?: string;
  /** Optional in SFMC. Only the summary's form asks for it. */
  postalcode?: string;
}

/**
 * A fresh id for this submission. It lands in the `id` column of the
 * `Emails_From_Branch` data extension, which is that table's **primary key** —
 * one row per signup. The ballot id is _not_ usable here: two readers can
 * share one ballot via the share link, and they would collide on the key,
 * silently overwriting each other. The ballot id goes in the `ballot` column,
 * which is what it is for.
 *
 * Mirrors the fallback in `getOrCreateLock()` — `crypto.randomUUID` is absent
 * on older Safari and on any non-secure origin.
 */
function newSubmissionId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Posts to the server-side relay (SFMC sends no CORS headers, so the browser
 * can't POST to it directly — see app/api/forms/voter-guide-signup/route.ts),
 * which stores the row and fires the confirmation journey that emails the
 * reader their ballot.
 */
export async function submitSignup(signup: Signup): Promise<void> {
  const params = new URLSearchParams({
    firstname: '',
    lastname: '',
    postalcode: '',
    ...signup,
    id: newSubmissionId(),
  });

  const res = await fetch(VOTER_GUIDE_SFMC_FORM_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });

  if (!res.ok) throw new Error('Voter Guide signup failed');
}

/**
 * An email the election hub's banner collected, waiting for VoterGuide2026 to
 * build the ballot the signup has to carry — the hub has no ballot to send.
 *
 * Held in memory rather than storage: the banner hands off with a client-side
 * `router.push`, so this module survives the trip, and it keeps working when
 * storage is blocked. Deliberately not a query param either — an email
 * address in the URL ends up in analytics and server logs.
 */
let heldEmail: string | null = null;

/** `null` drops a held email — each banner submit replaces the last. */
export function holdEmail(email: string | null) {
  heldEmail = email;
}

/** The held email, once. */
export function takeHeldEmail(): string | null {
  const email = heldEmail;
  heldEmail = null;
  return email;
}
